/**
 * Cookie/analytics consent.
 *
 * The banner has been in index.html since launch — markup, styles, buttons and
 * translations in all five locales — carrying the `hidden` attribute, and the
 * 2026-07-29 audit found that NOTHING in the repository ever removed it. So it
 * could not be shown, no choice was ever recorded, and both analytics layers
 * ran unconditionally: the Cloudflare beacon, and our own tracker which mints a
 * permanent id in localStorage and sends it with the visitor's IP and
 * user-agent. The privacy policy meanwhile promised a choice.
 *
 * Consent is now real: nothing analytics-related runs until the visitor has
 * decided, and "essential only" means exactly that — no beacon, no tracking id,
 * no /api/track calls. Essential cookies (the session) are unaffected; they are
 * required to deliver a service the user asked for.
 */
export type ConsentChoice = 'essential' | 'all';

const STORAGE_KEY = 'roamzy_consent_v1';

/** Read the stored decision, or null when the visitor has not chosen yet. */
export function getConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'all' || v === 'essential' ? v : null;
  } catch {
    // Storage blocked (private mode, embedded webview). Treat as "no consent":
    // the safe default is to collect nothing.
    return null;
  }
}

/** True only when the visitor actively opted into analytics. */
export function analyticsAllowed(): boolean {
  return getConsent() === 'all';
}

function store(choice: ConsentChoice): void {
  try { localStorage.setItem(STORAGE_KEY, choice); } catch { /* nothing we can do */ }
}

type ConsentListener = (choice: ConsentChoice) => void;
const listeners: ConsentListener[] = [];

/** Run `fn` once the visitor accepts analytics — immediately if already accepted. */
export function onAnalyticsAllowed(fn: () => void): void {
  if (analyticsAllowed()) { fn(); return; }
  listeners.push((choice) => { if (choice === 'all') fn(); });
}

/**
 * Show the banner when no decision exists and wire its two buttons.
 * Idempotent and safe to call before the router mounts.
 */
export function initConsent(): void {
  const banner = document.getElementById('cookie-banner');
  if (!banner) return;

  if (getConsent()) { banner.hidden = true; return; }
  banner.hidden = false;

  const decide = (choice: ConsentChoice): void => {
    store(choice);
    banner.hidden = true;
    for (const l of listeners.splice(0)) l(choice);
  };

  banner.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    if (t?.closest('[data-action="cookie-all"]')) { decide('all'); return; }
    if (t?.closest('[data-action="cookie-essential"]')) decide('essential');
  });
}
